import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { ActiveMedication } from "./components/active-medications";
import { CurrentRefill } from "./components/current-processing";
import { PastMedications } from "./components/past-medications";
import { EmptyMedicalCard } from "./components/empty-medical-cards";
import { MedicationHistory } from "./components/medical-history";
import { ReminderCard } from "./components/request-card";
import { getRouteApi } from "@tanstack/react-router";
import { PendingMedicationsPayments } from "./components/pending-payments-medications";
import {
    pendingMedicalsMedicationsQueryOptions,
    activeMedicalsMedicationsQueryOptions,
    pastMedicalsMedicationsQueryOptions,
    processingMedicalsMedicationsQueryOptions,
    medicalsMedicationsHistoryQueryOptions
} from "../api/queries/medications";
import { useEffect, useState } from "react";
import { MedicationPaymentErrorModal, MedicationPaymentSuccessModal } from "./components/payment-card";
import {
    PendingPaymentsSkeleton,
    ActiveMedicationsSkeleton,
    ProcessingMedicationsSkeleton,
    PastMedicationsSkeleton,
    MedicationHistorySkeleton,
} from "./components/skeletons";

export function MedicalsPage() {
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [showPaymentErrorModal, setShowPaymentErrorModal] = useState(false);
    const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<string | null>(null);
    const [paymentStatus, setPaymentStatus] = useState<"success" | "failed" | null>(null);

    const routeApi = getRouteApi("/(dashboard)/dashboard/medicals/$program/");
    const { prescription_id, payment_status } = routeApi.useSearch()
    const { program } = routeApi.useParams();


    useEffect(() => {
        if (prescription_id && payment_status === "success") {
            setSelectedPrescriptionId(prescription_id);
            setPaymentStatus("success");
            setShowPaymentSuccessModal(true);
            console.log("success", { prescription_id, payment_status })
        } else if (prescription_id && payment_status === "failed") {
            setSelectedPrescriptionId(prescription_id);
            setPaymentStatus("failed");
            setShowPaymentErrorModal(true);
            console.log("failed", { prescription_id, payment_status })
        }
    }, [prescription_id, payment_status]);

    const medicalsMedicationsHistoryQuery = useQuery(medicalsMedicationsHistoryQueryOptions({
        program: program === "weight-loss" ? "weight_loss" : "urgent_care",
        limit: 5,
        skip: 0
    }))

    const pendingPaymentsMedicalsMedicationsQuery = useSuspenseQuery(pendingMedicalsMedicationsQueryOptions({
        status: "pending_payment",
        program: program === "weight-loss" ? "weight_loss" : "urgent_care",
        limit: 5,
        skip: 0
    }))

    const activeMedicalsMedicationsQuery = useQuery(activeMedicalsMedicationsQueryOptions({
        status: "active",
        program: program === "weight-loss" ? "weight_loss" : "urgent_care",
        limit: 5,
        skip: 0
    }))

    const pastMedicalsMedicationsQuery = useQuery(pastMedicalsMedicationsQueryOptions({
        status: "past_medication",
        program: program === "weight-loss" ? "weight_loss" : "urgent_care",
        limit: 5,
        skip: 0
    }))

    const processingMedicalsMedicationsQuery = useQuery(processingMedicalsMedicationsQueryOptions({
        status: "processing",
        program: program === "weight-loss" ? "weight_loss" : "urgent_care",
        limit: 10,
        skip: 0
    }))

    return (
        <div className="flex flex-col gap-6">
            <section className="py-4 sm:py-10">
                <div className="lg:flex-row  flex flex-col sm:gap-4">
                    <div className="flex-1">
                        {pendingPaymentsMedicalsMedicationsQuery.isLoading ? (
                            <PendingPaymentsSkeleton />
                        ) : pendingPaymentsMedicalsMedicationsQuery.data && pendingPaymentsMedicalsMedicationsQuery.data.length > 0 ? (
                            <PendingMedicationsPayments medicalItems={pendingPaymentsMedicalsMedicationsQuery.data} totalCount={pendingPaymentsMedicalsMedicationsQuery.data.length ?? 0} />
                        ) : (
                            <EmptyMedicalCard
                                title="Pending payments"
                                icon={<h1 className="text-[32px]">💊</h1>}
                                description="Nothing yet"
                                message={`You have no pending payments for your ${program === "weight-loss" ? "weight loss" : "urgent care"} program.`}
                            />
                        )}

                        {activeMedicalsMedicationsQuery.isLoading ? (
                            <ActiveMedicationsSkeleton />
                        ) : activeMedicalsMedicationsQuery.data && activeMedicalsMedicationsQuery.data.length > 0 ? (
                            <ActiveMedication medicalItems={activeMedicalsMedicationsQuery.data} totalCount={activeMedicalsMedicationsQuery.data.length || 0} />
                        ) : (
                            <EmptyMedicalCard
                                title="Active medications"
                                icon={<h1 className="text-[32px]">💊</h1>}
                                description="Nothing yet"
                                message={`You have no active medications for your ${program === "weight-loss" ? "weight loss" : "urgent care"} program.`}
                            />
                        )}

                        {processingMedicalsMedicationsQuery.isLoading ? (
                            <ProcessingMedicationsSkeleton />
                        ) : processingMedicalsMedicationsQuery.data && processingMedicalsMedicationsQuery.data.length > 0 ? (
                            <CurrentRefill currentRefillsItems={processingMedicalsMedicationsQuery.data} totalCount={processingMedicalsMedicationsQuery.data.length || 0} />
                        ) : (
                            <EmptyMedicalCard
                                title="Currently Processing Medications"
                                icon={<h1 className="text-[32px]">⏳</h1>}
                                description="Nothing yet"
                                message={`You have no medications currently being processed for your ${program === "weight-loss" ? "weight loss" : "urgent care"} program.`}
                            />
                        )}

                        {pastMedicalsMedicationsQuery.isLoading ? (
                            <PastMedicationsSkeleton />
                        ) : pastMedicalsMedicationsQuery.data && pastMedicalsMedicationsQuery.data.data && pastMedicalsMedicationsQuery.data.data.length > 0 ? (
                            <PastMedications medicalItems={pastMedicalsMedicationsQuery.data.data} totalCount={pastMedicalsMedicationsQuery.data.data.length || 0} />
                        ) : (
                            <EmptyMedicalCard
                                title="Past medications"
                                icon={<h1 className="text-[32px]">📦</h1>}
                                description="Nothing yet"
                                message={`You have no past medications for your ${program === "weight-loss" ? "weight loss" : "urgent care"} program.`}
                            />
                        )}

                        {medicalsMedicationsHistoryQuery.isLoading ? (
                            <MedicationHistorySkeleton />
                        ) : medicalsMedicationsHistoryQuery.data && medicalsMedicationsHistoryQuery.data.length > 0 ? (
                            <MedicationHistory
                                medicationHistory={medicalsMedicationsHistoryQuery.data}
                                totalCount={medicalsMedicationsHistoryQuery.data.length || 0}
                            />
                        ) : (
                            <EmptyMedicalCard
                                title="Medication Refill History"
                                icon={<h1 className="text-[32px]">📂</h1>}
                                description="Nothing yet"
                                message="You currently do not have any history yet"
                            />
                        )}
                    </div>
                    <ReminderCard program={program as "weight-loss" | "urgent-care"} />
                </div>
            </section>

            {selectedPrescriptionId && showPaymentSuccessModal && paymentStatus === "success" && (
                <MedicationPaymentSuccessModal
                    open={showPaymentSuccessModal}
                    onClose={() => setShowPaymentSuccessModal(false)}
                    prescriptionId={selectedPrescriptionId}
                    program={program === "weight-loss" ? "weight loss" : "urgent care"}
                />
            )}

            {selectedPrescriptionId && showPaymentErrorModal && paymentStatus === "failed" && (
                <MedicationPaymentErrorModal
                    open={showPaymentErrorModal}
                    onClose={() => setShowPaymentErrorModal(false)}
                />
            )}
        </div>
    )
}

